import { useMemo, useState, useCallback, useEffect } from 'react'
import Image from 'next/image'
import useEmblaCarousel from 'embla-carousel-react'
import { SectionHeader } from '../SectionHeader'
import type { ComparisonGridData, ComparisonItem } from '@/data/solutions/types'

type ComparisonGridProps = ComparisonGridData

const iconMap: Record<string, string> = {
  check: '/assets/inc/comparison/green-check.svg',
  warn: '/assets/inc/comparison/yellow-warning.svg',
  close: '/assets/inc/comparison/red-cross.svg'
}

const mapAgencyText = (item: ComparisonItem) => item.agenciesText
const mapToolsText = (item: ComparisonItem) => item.toolsText

export const ComparisonGrid = ({
  heading,
  subtext,
  differentiators,
  comparison
}: ComparisonGridProps) => {
  const comparisonData = useMemo(() => comparison, [comparison])

  // Mobile carousel for cards
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start' })
  const [selectedIndex, setSelectedIndex] = useState(0)

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    onSelect()
    emblaApi.on('select', onSelect)
    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, onSelect])

  const scrollTo = useCallback(
    (index: number) => emblaApi?.scrollTo(index),
    [emblaApi]
  )
  const cards = [
    {
      title: 'Key Differentiator',
      bg: 'bg-white',
      border: 'border-[#E5E7EB]',
      shadow: 'shadow-[0_10px_24px_rgba(17,17,17,0.05)]',
      copy: differentiators.map(item => item.label),
      iconKey: 'differentiator',
      icons: differentiators.map(item => item.icon)
    },
    {
      title: 'Pepper',
      bg: 'bg-[#E0F5EB]',
      border: 'border-[#C8EBD8]',
      shadow: 'shadow-[0_14px_36px_rgba(24,121,78,0.12)]',
      copy: comparisonData.map(item => item.label),
      iconKey: 'pepper'
    },
    {
      title: 'Content Agencies',
      bg: 'bg-white',
      border: 'border-[#E5E7EB]',
      shadow: 'shadow-[0_10px_24px_rgba(17,17,17,0.05)]',
      copy: comparisonData.map(mapAgencyText),
      iconKey: 'agencies'
    },
    {
      title: 'Content Tools',
      bg: 'bg-white',
      border: 'border-[#E5E7EB]',
      shadow: 'shadow-[0_10px_24px_rgba(17,17,17,0.05)]',
      copy: comparisonData.map(mapToolsText),
      iconKey: 'tools'
    }
  ]

  return (
    <div className="space-y-8">
      <div className="text-center flex flex-col gap-2">
        <SectionHeader
          label="Pepper vs Others"
          icon={<></>}
          heading={heading}
          color="inc-green-50"
        />
        <p className="text-sm md:text-base">{subtext}</p>
      </div>

      {/* Mobile / tablet: carousel with all 4 columns */}
      <div className="lg:hidden space-y-4">
        <div
          className="grid"
          style={{
            gridTemplateRows: `repeat(${comparisonData.length + 1}, auto)`
          }}
        >
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-4 px-4">
              {cards.map(card => (
                <div
                  key={card.title}
                  className={`flex-[0_0_85%] rounded-3xl border p-6 grid gap-y-4 grid-cols-1 row-span-full grid-rows-subgrid ${card.bg} ${card.border}`}
                >
                  <div
                    className={`flex items-center gap-2 text-lg font-semibold pb-2 row-[1/2] col-[1/2]`}
                  >
                    {card.title}
                  </div>
                  {card.copy.map((text, idx) => (
                    <div
                      key={idx}
                      className={`flex gap-3 items-start text-sm row-[${
                        idx + 2
                      }/${idx + 3}] col-[1/2]`}
                    >
                      {card.iconKey === 'differentiator' ? (
                        <img
                          src={card.icons?.[idx]}
                          alt=""
                          className="w-[18px] h-[18px] mt-0.5"
                        />
                      ) : (
                        <Image
                          src={
                            card.iconKey === 'pepper'
                              ? iconMap[comparisonData[idx].pepper]
                              : card.iconKey === 'agencies'
                              ? iconMap[comparisonData[idx].agencies]
                              : iconMap[comparisonData[idx].tools]
                          }
                          alt=""
                          width={18}
                          height={18}
                        />
                      )}
                      <p>{text}</p>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Carousel indicators */}
        <div className="flex items-center justify-center gap-2">
          {cards.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollTo(index)}
              aria-label={`Go to slide ${index + 1}`}
              className={`w-2 h-2 rounded-full transition-all ${
                selectedIndex === index ? 'bg-black' : 'border border-black'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Desktop: subgrid layout */}
      <div
        className={`hidden lg:grid grid-cols-4 grid-rows-[repeat(${
          differentiators.length + 1
        },auto)] gap-6`}
        style={{
          gridTemplateRows: `repeat(${differentiators.length + 1},auto)`
        }}
      >
        <ul className="p-6 | grid grid-cols-1 row-span-full grid-rows-subgrid">
          <li className="row-[1/2] col-[1/2]">
            <h3 className="font-semibold">Key Differentiator</h3>
          </li>
          {differentiators.map((item, index) => (
            <li
              key={item.label}
              className={`flex items-center gap-3 row-[${index + 2}/${
                index + 3
              }] col-[1/2]`}
            >
              <img src={item.icon} alt="icon" className="size-6" />
              {item.label}
            </li>
          ))}
        </ul>

        <ul className="p-6 | grid grid-cols-1 row-span-full grid-rows-subgrid | rounded-3xl border border-inc-green-20 bg-inc-green-10">
          <li>
            <h3 className="font-semibold">Pepper</h3>
          </li>
          {comparisonData.map((item, index) => (
            <li
              key={item.label}
              className={`flex items-start gap-3 row-[${index + 2}/${
                index + 3
              }] col-[1/2]`}
            >
              <Image
                src={iconMap[item.pepper]}
                alt=""
                width={18}
                height={18}
                className="translate-y-1"
              />
              <p>{item.label}</p>
            </li>
          ))}
        </ul>

        <ul
          className="p-6 | grid grid-cols-1 row-span-full grid-rows-subgrid | rounded-3xl border borde
         r-[#E5E7EB]"
        >
          <li>
            <h3 className="font-semibold">Content Agencies</h3>
          </li>
          {comparisonData.map((item, index) => (
            <li
              key={item.label}
              className={`flex items-start gap-3 row-[${index + 2}/${
                index + 3
              }] col-[1/2]`}
            >
              <Image
                src={iconMap[item.agencies]}
                alt=""
                width={18}
                height={18}
                className="translate-y-1"
              />
              <p>{mapAgencyText(item)}</p>
            </li>
          ))}
        </ul>

        <ul
          className="p-6 | grid row-span-full grid-rows-subgrid | rounded-3xl border borde
         r-[#E5E7EB]"
        >
          <li>
            <h3 className="font-semibold">Content Tools</h3>
          </li>
          {comparisonData.map(item => (
            <li key={item.label} className="flex gap-3 items-start">
              <Image
                src={iconMap[item.tools]}
                alt=""
                width={18}
                height={18}
                className="translate-y-1"
              />
              <p>{mapToolsText(item)}</p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
